import React, { useState } from 'react';
import { Package } from 'lucide-react';
import EntityInspector from '../ui/EntityInspector';
import StatusBadge from '../ui/StatusBadge';
import ChannelBadge from '../ui/ChannelBadge';
import Button from '../ui/Button';
import EditarContactoPedido from '../EditarContactoPedido';
import OrigenCompra from '../OrigenCompra';
import RecorridoCompra from '../RecorridoCompra';
import ReciboWhatsApp from './ReciboWhatsApp'; // comprobante-whatsapp: reenviar + registro de envíos
import {
  statusDef, normalizeSalesChannel, fmtMoney,
  getPaymentLabel, getOrderOrigin, formatOrderDate,
} from '../../utils/orderUtils';
import { OrderItems, OrderTotals, customerName, hasPendingShipping } from './orderInspectorShared';
import { propsSoloLectura } from '../../utils/soloLectura';
import '../../pages/OrdersPage.css';

// Estados que el selector OFRECE: los NO-destructivos del ciclo de vida. `cancelled` y
// `refunded` quedan FUERA a propósito (Miguel 2026-08-25): cancelar restaura stock y refunded
// tiene implicaciones contables — no pueden ser un clic en un dropdown; siguen por el botón
// «Cancelar pedido» con su confirmación. `failed` tampoco se ofrece (sin caso de uso).
// Se EXPORTA (una sola definición) para que OrderListInspector valide contra la misma lista.
export const STATUS_SELECCIONABLES = ['pending', 'on-hold', 'processing', 'completed'];

/* Drawer del pedido — EntityInspector PLANO (F3.2), EXTRAÍDO VERBATIM de
   OrderListInspector (F5#4) para compartirlo también con Caja. El cuerpo (orden
   canónico productos→ticket→pago→envío) no cambia. Las acciones son OPCIONALES:
   con `onEdit`/`onManageShipping`/`onRequestCancel` es la superficie de Pedidos y
   Resumen; sin ellas, es de LECTURA (Caja abre una venta, no la edita/cancela).
   El ConfirmDialog de cancelación vive en el padre (lo comparte con el ⋮ de la
   tabla): acá el botón solo dispara `onRequestCancel(order)`. */
export default function OrderInspectorDrawer({
  selected,
  orders = [],
  onSelect,
  isVendedor = false,
  soloLectura = false,
  onEdit = null,
  onManageShipping = null,
  onRequestCancel = null,
  onChangeStatus = null,
  onContactoGuardado = null, // P2: si se pasa, muestra «Editar contacto» y refresca al guardar
  overlayClassName = '',
}) {
  const [editandoContacto, setEditandoContacto] = useState(false);
  const selectedIdx = selected ? orders.findIndex((o) => o.id === selected.id) : -1;
  // P2: el contacto se puede editar en CUALQUIER pedido menos en la papelera (POS, web e invitados).
  // Disponible para todos los roles que abren el detalle (el backend valida rol y estado).
  const puedeEditarContacto = !!onContactoGuardado && selected && selected.status !== 'trash';
  const puedeCancelar = onRequestCancel && selected && selected.status !== 'cancelled' && selected.status !== 'refunded';
  // El selector de estado solo donde se pasa `onChangeStatus` (Pedidos, no el modo lectura de
  // Caja) y sobre un pedido no-destructivo (cancelled/refunded no se re-activan desde el dropdown).
  const puedeCambiarEstado = !isVendedor && !!onChangeStatus && selected
    && selected.status !== 'cancelled' && selected.status !== 'refunded';
  const hayAcciones = !isVendedor && (onEdit || puedeCancelar);

  return (
    <EntityInspector
      open={!!selected}
      mode="drawer"
      overlayClassName={overlayClassName}
      title={selected ? `Pedido #${selected.number}` : ''}
      subtitle={selected ? `${formatOrderDate(selected.date_created)} · ${customerName(selected)}` : ''}
      status={selected && <>
        <StatusBadge variant={statusDef(selected.status).variant}>{statusDef(selected.status).label}</StatusBadge>
        <ChannelBadge channel={normalizeSalesChannel(selected).key} />
      </>}
      headerAction={selected && onManageShipping && hasPendingShipping(selected) && (
        <Button size="sm" variant="primary" icon={<Package size={14} />} {...propsSoloLectura(soloLectura)} onClick={() => onManageShipping?.(selected)}>Gestionar envío</Button>
      )}
      actions={hayAcciones && <>
        {/* Regla del rojo (Miguel 2026-07-23): cancelar/anular = destructivo SIEMPRE */}
        {puedeCancelar && (
          <Button variant="destructive" {...propsSoloLectura(soloLectura)} onClick={() => onRequestCancel(selected)}>Cancelar pedido</Button>
        )}
        {onEdit && (
          <Button variant="primary" {...propsSoloLectura(soloLectura)} onClick={() => { onSelect?.(null); onEdit(selected); }}>Editar pedido</Button>
        )}
      </>}
      onClose={() => onSelect?.(null)}
      onPrev={selectedIdx > 0 ? () => onSelect?.(orders[selectedIdx - 1]) : null}
      onNext={selectedIdx >= 0 && selectedIdx < orders.length - 1 ? () => onSelect?.(orders[selectedIdx + 1]) : null}
    >
      {selected && (
        <div className="op-inspector-body">
          {puedeCambiarEstado && (
            <div className="op-status-changer">
              {/* Advertencia FUERTE (Miguel 2026-08-26): «completado» es la señal de despachado
                  —el flujo de envío hace processing→completed—, y la cola de por-despachar exige
                  processing. Completar acá saca el pedido de esa cola: nadie más lo ve para
                  despachar (el costo sin registrar es el efecto SECUNDARIO). Se enuncia la
                  CONSECUENCIA, no una prohibición: hay casos legítimos (despacho hecho por fuera). */}
              {hasPendingShipping(selected) && (
                <p className="op-status-changer__alert">
                  ⚠️ Este pedido tiene <strong>envío pendiente</strong>. Si lo completas desde aquí,
                  {' '}<strong>sale de la cola de envíos pendientes</strong> y nadie más lo verá para
                  despachar; además su costo de mensajero queda sin registrar. Para despacharlo,
                  usa <strong>Envíos</strong>.
                </p>
              )}
              <label className="op-status-changer__label" htmlFor="op-status-select">Cambiar estado del pedido</label>
              <select
                id="op-status-select"
                className="op-status-changer__select"
                value={selected.status}
                {...propsSoloLectura(soloLectura)}
                onChange={(e) => onChangeStatus(selected, e.target.value)}
              >
                {/* Si el estado actual no es uno de los ofrecidos (p. ej. failed), se muestra
                    como opción deshabilitada para no perder el valor actual del select. */}
                {!STATUS_SELECCIONABLES.includes(selected.status) && (
                  <option value={selected.status} disabled>{statusDef(selected.status).label} (actual)</option>
                )}
                {STATUS_SELECCIONABLES.map((s) => <option key={s} value={s}>{statusDef(s).label}</option>)}
              </select>
              {/* Advertencias VISIBLES en el momento (Miguel 2026-08-25), no solo en el código:
                  el efecto contable de bajar una venta y la notificación al cliente son cosas
                  que nadie anticipa y aparecen después como descuadre/sorpresa. */}
              <p className="op-status-changer__hint">
                Cambiar a <strong>Procesando</strong> o <strong>Completado</strong> puede notificar al cliente
                (igual que en WooCommerce). Bajar una venta confirmada a <strong>Pendiente</strong> o
                {' '}<strong>En espera</strong> no elimina su movimiento de caja automáticamente.
              </p>
            </div>
          )}
          {/* Orden canónico (principio de las tres superficies):
             QUÉ → CUÁNTO (ticket) → CÓMO → ENVÍO */}
          <OrderItems order={selected} />
          <div className="op-ticket"><OrderTotals order={selected} /></div>
          <h4 className="op-insp-h">Pago y canal</h4>
          <div className="op-row__meta">
            <div className="op-meta-item"><span className="op-meta-label">Pago</span><span>{getPaymentLabel(selected).label}</span></div>
            <div className="op-meta-item"><span className="op-meta-label">Canal</span><span>{getOrderOrigin(selected).label}</span></div>
            {selected.customer_note && (
              <div className="op-meta-item op-meta-item--wide"><span className="op-meta-label">Notas del cliente</span><span>{selected.customer_note}</span></div>
            )}
          </div>
          <h4 className="op-insp-h">Envío</h4>
          <div className="op-row__meta">
            <div className="op-meta-item"><span className="op-meta-label">Estado del envío</span>
              <span>{selected.estado_envio || (selected.shipping_lines?.length ? 'Pendiente' : 'Sin envío')}</span>
            </div>
            {parseFloat(selected.shipping_total || 0) > 0 && (
              <div className="op-meta-item"><span className="op-meta-label">Cobrado al cliente</span><span>{fmtMoney(parseFloat(selected.shipping_total))}</span></div>
            )}
            {(selected.shipping?.address_1 || selected.billing?.address_1) && (
              <div className="op-meta-item op-meta-item--wide"><span className="op-meta-label">Dirección</span>
                <span>{[
                  selected.shipping?.address_1 || selected.billing?.address_1,
                  selected.shipping?.city || selected.billing?.city,
                ].filter(Boolean).join(', ')}</span>
              </div>
            )}
          </div>
          {/* comprobante-whatsapp (remate 20-oct): el bloque va ARRIBA, pegado a envío/contacto —
             es una ACCIÓN sobre el pedido, no historia; al final nadie lo encontraba. */}
          <ReciboWhatsApp order={selected} />
          {/* pedidos-origen N1: de dónde llegó esta compra (fetch propio al espejo; traducción
             en el componente). Va después de Envío — cierra la historia del pedido. */}
          <OrigenCompra orderId={selected.id} variante="drawer" />
          {/* sensor-recorrido N2: la línea de tiempo de esta compra, debajo del origen.
             Para un rol sin acceso (403) el bloque no existe — lo decide el componente. */}
          <RecorridoCompra orderId={selected.id} variante="drawer" />
          {/* P2: corregir el CONTACTO de este pedido (nombre/correo/tel/cédula/dirección). Sale en
             TODOS los pedidos menos papelera —POS, web e invitados—. Distinto de «Editar pedido»
             (que cambia líneas y es solo admin/coordinador). */}
          {puedeEditarContacto && (
            <div className="op-insp-contacto">
              <Button size="sm" variant="primary" {...propsSoloLectura(soloLectura)} onClick={() => setEditandoContacto(true)}>Editar contacto</Button>
            </div>
          )}
        </div>
      )}
      {editandoContacto && selected && (
        <EditarContactoPedido
          order={selected}
          onCancel={() => setEditandoContacto(false)}
          onGuardado={(updated) => { setEditandoContacto(false); if (updated) { onSelect?.(updated); onContactoGuardado?.(updated); } }}
        />
      )}
    </EntityInspector>
  );
}
